import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-8" data-testid="back-link">
          <ArrowLeft size={16} /> Back
        </Link>

        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: April 2026 | DocSite — A product of ChainX Tech Labs</p>

        <div className="prose prose-sm prose-slate max-w-none space-y-6 text-slate-700 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Information We Collect</h2>
            <p><strong>Doctors:</strong> Name, email, phone number, qualifications, experience, practice locations, procedures, photos, OTP verification data, IP address, user agent, consent timestamps.</p>
            <p><strong>Patients:</strong> Name, email, phone number (optional), appointment history, clinical feedback responses, IP address, user agent.</p>
            <p><strong>Automatically:</strong> Browser type, device information, IP address, access timestamps, pages visited.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>2. How We Use Your Information</h2>
            <p>We use your information to: (a) operate and maintain the platform; (b) display doctor profiles and websites; (c) facilitate appointment scheduling; (d) process payments and subscriptions; (e) generate clinical ratings from patient feedback; (f) send transactional communications (receipts, reminders); (g) ensure security and prevent fraud; (h) comply with legal obligations.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>3. Data Sharing</h2>
            <p>We do not sell your personal data. We share data only: (a) with the doctor you book an appointment with (patient data); (b) with payment processors for subscription payments; (c) when required by law or legal process; (d) to protect the rights and safety of our users.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Data Security</h2>
            <p>We implement industry-standard security measures including: HTTPS encryption in transit, password hashing (bcrypt), JWT-based authentication, OTP verification for doctor registration, IP/audit logging for consent records.</p>
            <p>Premium doctors can export their interaction data in Fernet-encrypted (AES-128 CBC + HMAC-SHA256) format. Patients can download their own interaction history at any time. No personal health records or clinical data is stored on DocSite — only scheduling and interaction data. The encryption key is derived from the doctor's own passphrase and is not stored by DocSite.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Data Retention</h2>
            <p>We retain your data for as long as your account is active or as needed to provide services. Appointment records and feedback are retained for legal and medical record-keeping purposes. You may request deletion of your account and data (see Section 7).</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Cookies & Local Storage</h2>
            <p>We use browser local storage for authentication tokens and PWA offline caching. We do not use third-party tracking cookies. The service worker caches doctor website pages for offline access.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Your Rights</h2>
            <p>You have the right to: (a) access your personal data; (b) correct inaccurate data; (c) request deletion of your data; (d) withdraw consent for data processing; (e) receive a copy of your data in a portable format (doctors: via encrypted export).</p>
            <p>To exercise these rights, contact us at <strong>vt@chainx.live</strong>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Compliance</h2>
            <p>DocSite is designed with data protection principles in mind, aligned with: (a) India's Digital Personal Data Protection Act 2023 (DPDP); (b) General Data Protection Regulation (GDPR) where applicable; (c) Health data handling best practices.</p>
            <p>We are continuously improving our compliance posture. For specific compliance inquiries, contact <strong>vt@chainx.live</strong>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Children's Privacy</h2>
            <p>DocSite is not intended for use by individuals under 18 years of age. We do not knowingly collect personal data from children.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify registered users of material changes via email or platform notification. Continued use of the platform after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Contact</h2>
            <p>Data Protection Officer: <strong>vt@chainx.live</strong></p>
            <p>ChainX Tech Labs, India</p>
          </section>

        </div>
      </div>
    </div>
  );
}
